import { Fragment, PureComponent, ReactNode } from 'react';
import { Button, Radio, Form, Input, notification } from 'antd';
import AppLoader from '../../components/common/AppLoader';
import { BankAccountsComponent } from '../../components/BankAccounts';
import getBankAccounts, { Response } from '../../api/getBankAccounts';
import { captureError, captureErrorString } from '../captureError';
import { track } from '../../tracking';
import addBankAccount from '../../api/addBankAccount';
import getValidationStatus from '../../api/getValidationStatus';
import ValidatedBankDetails from '../heOnboarding/ValidatedBankDetails';
import markBankAccountValidated from '../../api/markBankAccountValidated';

type State = {
    saveApiStatus?: 'loading' | 'success' | 'error';
    fetchingBankAccounts: boolean;
    bankAccounts: Response[];
    validatedBankDetails?: { account_number: string; registered_name: string };
    fund_acc_id?: string;
    isMarkingValidated?: boolean;
    isVerifyingBankDetails?: boolean;
};

interface Props {
    children?: React.ReactNode;
}

class ActivatePayments extends PureComponent<Props, State> {
    state: State = {
        fetchingBankAccounts: true,
        bankAccounts: [],
    };
    polling?: NodeJS.Timeout = undefined;
    POLL_INTERVAL_MS = 10 * 1000;

    onFinish = async (values: {
        account_number: string;
        account_type: string;
        beneficiary_name: string;
        email: string;
        ifsc_code: string;
    }): Promise<void> => {
        const { account_number, account_type, beneficiary_name, email, ifsc_code } = values;
        this.setState({ saveApiStatus: 'loading' });
        const requestPayload = {
            account_type: 'bank_account',
            account_details: {
                name: beneficiary_name,
                ifsc: ifsc_code,
                account_number: account_number,
                account_type: account_type,
                email: email,
            },
        };

        try {
            const { success, response } = await addBankAccount(requestPayload);
            const { error } = response;
            if (success) {
                const { fund_acc_id } = response;
                if (fund_acc_id && account_number) {
                    this.poll(fund_acc_id, account_number);
                    this.polling = setInterval(
                        () => this.poll(fund_acc_id, account_number),
                        this.POLL_INTERVAL_MS,
                    );
                }
                this.setState({ saveApiStatus: 'success', isVerifyingBankDetails: true });
                track('ADD_BANK_DETAILS', { fund_acc_id, account_number });
            } else {
                this.setState({ saveApiStatus: 'error' });
                notification.error({
                    message: error?.description || 'Error while validating bank details!',
                    duration: 5,
                });
                track('ERROR', {
                    on: 'addBankDetails',
                    error: error?.description,
                });
                captureErrorString('Failed to add bank account: ' + JSON.stringify(response));
            }
        } catch (e) {
            notification.error({
                message: 'Error while adding bank details!',
                duration: 5,
            });
            captureErrorString((e as Error).message);
            this.setState({ saveApiStatus: 'error' });
        }
    };

    poll = async (fund_acc_id: string, account_number: string): Promise<void> => {
        try {
            const { response } = await getValidationStatus(fund_acc_id);
            const { validation_details } = response;
            const { registered_name } = validation_details;

            if (response.status === 'validation_completed' || response.status === 'validated') {
                if (this.polling) {
                    clearInterval(this.polling);
                }
                if (!registered_name) {
                    notification.error({
                        message: `Please enter valid ${
                            account_number ? 'bank account' : 'upi id'
                        } details!`,
                        duration: 5,
                    });
                    this.setState({
                        isVerifyingBankDetails: false,
                    });
                    return;
                }
                this.setState({
                    isVerifyingBankDetails: false,
                    validatedBankDetails: {
                        registered_name,
                        account_number,
                    },
                    fund_acc_id,
                });
            }
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'getValidationStatus',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while validating bank details!',
                duration: 5,
            });
            this.setState({
                isVerifyingBankDetails: false,
            });
        }
    };

    componentDidMount = (): void => {
        this.getAllBankAccounts();
    };

    getAllBankAccounts = async (): Promise<void> => {
        this.setState({ fetchingBankAccounts: true });
        try {
            const { success, response, error = '' } = await getBankAccounts(true);
            if (success && response) {
                if (Array.isArray(response)) {
                    this.setState({ bankAccounts: response });
                }
            } else {
                captureErrorString(error);
                notification.error({
                    message: error || 'Something went wrong. Please try again',
                    duration: 5,
                });
                track('ERROR', { on: 'getBankAccounts', what: error });
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'getBankAccounts',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Something went wrong. Please try again',
                duration: 5,
            });
        } finally {
            this.setState({ fetchingBankAccounts: false });
        }
    };

    onClickHandler = async (): Promise<void> => {
        try {
            this.setState({
                isMarkingValidated: true,
            });
            const { fund_acc_id } = this.state;
            if (fund_acc_id) {
                const { response } = await markBankAccountValidated(fund_acc_id);
                if (!response.success) {
                    notification.error({
                        message: 'Error while validating details!',
                        duration: 5,
                    });
                    return;
                }
                this.getAllBankAccounts();
            } else {
                notification.error({
                    message: 'Error while validating details!',
                    duration: 5,
                });
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'markBankAccountValidated',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error while validating details!',
                duration: 5,
            });
        } finally {
            this.setState({
                isMarkingValidated: false,
                validatedBankDetails: undefined,
            });
        }
    };

    render(): ReactNode {
        const {
            fetchingBankAccounts,
            bankAccounts,
            saveApiStatus,
            validatedBankDetails,
            isMarkingValidated,
            isVerifyingBankDetails,
        } = this.state;
        const { onClickHandler } = this;
        // const isStaff = getDoc()?.loginmeta?.role === 'STAFF';

        return (
            <div className="flex flex-col w-full h-full">
                <p className="text-16 font-700 text-neutral-black px-24 pb-0 pt-24">
                    Activate Payments
                </p>
                <div className="m-16 bg-white p-8 flex-1 overflow-auto">
                    {fetchingBankAccounts ? (
                        <div className="flex h-full flex-1 justify-center items-center">
                            <AppLoader />
                        </div>
                    ) : bankAccounts.length > 0 ? (
                        <Fragment>
                            <div className="flex justify-between bg-darwin-accent-green-200 items-center py-14 pl-12 pr-24 w-full border rounded-8 border-darwin-accent-green-900">
                                <div className="flex items-top">
                                    <div className="w-20 h-20 flex justify-center items-center mt-8">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                            className="w-20 h-20 fill-status-success"
                                        >
                                            <path d="M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM371.8 211.8C382.7 200.9 382.7 183.1 371.8 172.2C360.9 161.3 343.1 161.3 332.2 172.2L224 280.4L179.8 236.2C168.9 225.3 151.1 225.3 140.2 236.2C129.3 247.1 129.3 264.9 140.2 275.8L204.2 339.8C215.1 350.7 232.9 350.7 243.8 339.8L371.8 211.8z"></path>
                                        </svg>
                                    </div>
                                    <div className="ml-14 font-400 text-14 leading-tight space-y-6">
                                        <p className="pb-3 font-700 text-16">Payments activated!</p>
                                        <p>
                                            You can now recieve payments via Eka for your
                                            appointments, bills and more. It takes 2 working days
                                            for Account settlements, irrespective of your settlement
                                            schedule.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <BankAccountsComponent
                                isLoading={fetchingBankAccounts}
                                bankAccounts={bankAccounts}
                                displayText="doctor"
                            />
                            {/* {isStaff && <BankAccounts displayText="staff" />} */}
                        </Fragment>
                    ) : (
                        <Fragment>
                            <div className="flex flex-col space-y-16">
                                <div className="mb-8 flex relative justify-between bg-darwin-primary-100 items-center py-14 pl-12 pr-24 w-full border rounded-8 border-darwin-accent-bright-blue-800">
                                    <div className="flex items-top">
                                        <div className="mt-4 uppercase max-h-24 text-12 font-700 text-white px-11 py-3 bg-darwin-accent-medical-history-orange-800 rounded-4">
                                            New
                                        </div>
                                        <div className="ml-14 font-400 text-14 leading-tight">
                                            <p className="pb-3 font-700 text-16">
                                                Activate payments to accept online payments!
                                            </p>
                                            <p>
                                                Once you activate payments you can accept pre-paid
                                                appointment bookings on your eka appointment link!
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <span className="text-16 font-700">Bank Details</span>
                                <Form
                                    name="basic"
                                    wrapperCol={{ span: 8 }}
                                    initialValues={{ account_type: 'current' }}
                                    onFinish={this.onFinish}
                                    layout="vertical"
                                    autoComplete="off"
                                >
                                    <Form.Item
                                        label="Beneficiary Name"
                                        name="beneficiary_name"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please input your Beneficiary Name!',
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>

                                    <Form.Item
                                        label="Account Number"
                                        name="account_number"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please input your Account Number!',
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>

                                    <Form.Item
                                        label="Account Type"
                                        name="account_type"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please select your Account type!',
                                            },
                                        ]}
                                    >
                                        <Radio.Group>
                                            <Radio.Button value="current">Current</Radio.Button>
                                            <Radio.Button value="savings">Saving</Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>

                                    <Form.Item
                                        label="IFSC Code"
                                        name="ifsc_code"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please input your IFSC code!',
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>

                                    <Form.Item
                                        label="Email"
                                        name="email"
                                        rules={[
                                            {
                                                type: 'email',
                                                message: 'The input is not valid E-mail!',
                                            },
                                            {
                                                required: true,
                                                message: 'Please input your email!',
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            className="primary-btn font-lato cursor-pointer"
                                            loading={
                                                saveApiStatus === 'loading' ||
                                                isVerifyingBankDetails
                                            }
                                        >
                                            Submit Details
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </div>
                            {/* {isStaff && <BankAccounts displayText="staff" />} */}
                        </Fragment>
                    )}
                </div>
                {validatedBankDetails && (
                    <ValidatedBankDetails
                        validatedBankDetails={validatedBankDetails}
                        onClose={() => this.setState({ validatedBankDetails: undefined })}
                        onConfirm={onClickHandler}
                        isSavingProfile={isMarkingValidated}
                    />
                )}
            </div>
        );
    }
}

export default ActivatePayments;
